"use client";

import { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "@/lib/db";
import type { AiProfile } from "@/types/ai";
import type { Project } from "@/types/project";
import type { ChatMessage } from "@/lib/db/schema";
import type { Channel, TeamMessage } from "@/lib/db/schema";
import type { Team } from "@/types/collaboration";
import { X, MessageSquare, FolderKanban, Plus, Clock, Bot, Users, Hash, Search, MoreVertical, Copy, Trash2, ChevronDown, ChevronUp, MessageSquareMore } from "lucide-react";
import { getUserId } from "@/lib/user/identity";
import AISelector from "./AISelector";

interface Props {
  profile: AiProfile;
  isOpen: boolean;
  onClose: () => void;
  onSelectProject?: (projectId: string) => void;
  onNewChat?: () => void;
  onSelectAI?: (aiId: string) => void;
  onSelectTeamChannel?: (teamId: string, channelId: string) => void;
  hasMultiChat?: boolean; // Whether user has purchased multi-chat neuroblock
}

type ProjectStatus = "active" | "completed" | "archived";

interface ProjectWithStatus extends Project {
  status: ProjectStatus;
}

export default function ChatSidebar({ 
  profile, 
  isOpen, 
  onClose, 
  onSelectProject, 
  onNewChat,
  onSelectAI,
  onSelectTeamChannel,
  hasMultiChat = false,
}: Props) {
  const router = useRouter();
  const [projects, setProjects] = useState<Project[]>([]);
  const [directChats, setDirectChats] = useState<Array<{ id: string; preview: string; timestamp: number; aiId: string; aiName?: string }>>([]);
  const [groupChats, setGroupChats] = useState<Array<{ teamId: string; channelId: string; name: string; lastMessage: string; timestamp: number }>>([]);
  const [allAIs, setAllAIs] = useState<AiProfile[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [activeTab, setActiveTab] = useState<"projects" | "chats" | "ai">("projects");
  const [searchQuery, setSearchQuery] = useState("");
  const [showDirectChats, setShowDirectChats] = useState(true);
  const [showTeamChats, setShowTeamChats] = useState(true);
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, profile.id]);

  // Infer project status based on activity
  function getProjectStatus(project: Project): ProjectStatus {
    const daysSinceUpdate = (Date.now() - project.updatedAt) / (1000 * 60 * 60 * 24);
    if (daysSinceUpdate < 7) return "active";
    if (daysSinceUpdate < 30) return "completed";
    return "archived";
  }

  async function loadData() {
    try {
      if (!db) {
        console.warn("Database not initialized yet");
        return;
      }

      const userId = getUserId();

      // Load projects
      if (db.projects) {
        const projs = await db.projects
          .where("profileId")
          .equals(profile.id)
          .sortBy("updatedAt");
        setProjects(projs.reverse().slice(0, 20)); // Show more projects
      }

      // Load all AIs for the user
      if (db.aiProfiles) {
        const all = await db.aiProfiles
          .where("ownerId")
          .equals(userId)
          .sortBy("updatedAt");
        setAllAIs(all.reverse());
      }

      // Load direct chats (chats with this specific AI and group by AI)
      if (db.chatMessages && db.aiProfiles) {
        // Get all messages for all AIs
        const allAIMessages: Array<{ message: ChatMessage; aiId: string; aiName: string }> = [];
        
        for (const ai of allAIs) {
          const messages = await db.chatMessages
            .where("profileId")
            .equals(ai.id)
            .sortBy("createdAt");
          
          const recentMessages = messages.slice(-10).reverse();
          recentMessages.forEach((msg) => {
            if (msg.role === "user") {
              allAIMessages.push({
                message: msg,
                aiId: ai.id,
                aiName: ai.name,
              });
            }
          });
        }

        // Sort by timestamp and group by AI
        allAIMessages.sort((a, b) => b.message.createdAt - a.message.createdAt);
        
        const uniqueChats = new Map<string, { preview: string; timestamp: number; aiId: string; aiName: string }>();
        allAIMessages.forEach(({ message, aiId, aiName }) => {
          if (!uniqueChats.has(`${aiId}-${message.id}`)) {
            uniqueChats.set(`${aiId}-${message.id}`, {
              preview: message.content.substring(0, 50) + (message.content.length > 50 ? "..." : ""),
              timestamp: message.createdAt,
              aiId,
              aiName,
            });
          }
        });
        
        setDirectChats(Array.from(uniqueChats.values()).slice(0, 20));
      }

      // Load teams and channels for group chats
      if (db.teams && db.channels && db.teamMessages) {
        const userTeams = await db.teams
          .where("ownerId")
          .equals(userId)
          .toArray();
        
        if (db.teamMembers) {
          const memberEntries = await db.teamMembers
            .where("userId")
            .equals(userId)
            .toArray();
          
          const memberTeamIds = memberEntries.map(m => m.teamId);
          const memberTeams = await Promise.all(
            memberTeamIds.map(id => db.teams.get(id))
          );
          
          const allTeams = [...userTeams, ...memberTeams.filter(Boolean)] as Team[];
          setTeams(allTeams);

          const allChannels: Channel[] = [];
          for (const team of allTeams) {
            const teamChannels = await db.channels
              .where("teamId")
              .equals(team.id)
              .toArray();
            allChannels.push(...teamChannels);
          }
          setChannels(allChannels);

          const groupChatData: Array<{ teamId: string; channelId: string; name: string; lastMessage: string; timestamp: number }> = [];
          
          for (const channel of allChannels) {
            const team = allTeams.find(t => t.id === channel.teamId);
            const channelMessages = await db.teamMessages
              .where("channelId")
              .equals(channel.id)
              .sortBy("createdAt");
            
            if (channelMessages.length > 0) {
              const lastMessage = channelMessages[channelMessages.length - 1];
              groupChatData.push({
                teamId: channel.teamId,
                channelId: channel.id,
                name: `#${channel.name}${team ? ` • ${team.name}` : ""}`,
                lastMessage: lastMessage.content.substring(0, 40) + (lastMessage.content.length > 40 ? "..." : ""),
                timestamp: lastMessage.createdAt,
              });
            }
          }

          groupChatData.sort((a, b) => b.timestamp - a.timestamp);
          setGroupChats(groupChatData.slice(0, 10));
        }
      }
    } catch (error) {
      console.error("Failed to load sidebar data:", error);
      setProjects([]);
      setDirectChats([]);
      setGroupChats([]);
    }
  }

  // Filter projects by search
  const filteredProjects = useMemo(() => {
    if (!searchQuery) return projects;
    const query = searchQuery.toLowerCase();
    return projects.filter(p => 
      p.name.toLowerCase().includes(query) || 
      p.description?.toLowerCase().includes(query)
    );
  }, [projects, searchQuery]);

  // Filter chats by search and group by AI
  const filteredDirectChats = useMemo(() => {
    let filtered = directChats;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = directChats.filter(c => 
        c.preview.toLowerCase().includes(query) ||
        c.aiName?.toLowerCase().includes(query)
      );
    }
    
    // Group by AI
    const grouped = new Map<string, typeof filtered>();
    filtered.forEach(chat => {
      if (!grouped.has(chat.aiId)) {
        grouped.set(chat.aiId, []);
      }
      grouped.get(chat.aiId)!.push(chat);
    });
    
    return grouped;
  }, [directChats, searchQuery]);

  const filteredGroupChats = useMemo(() => {
    if (!searchQuery) return groupChats;
    const query = searchQuery.toLowerCase();
    return groupChats.filter(c => 
      c.name.toLowerCase().includes(query) ||
      c.lastMessage.toLowerCase().includes(query)
    );
  }, [groupChats, searchQuery]);

  async function handleDuplicateProject(projectId: string, e: React.MouseEvent) {
    e.stopPropagation();
    try {
      const project = projects.find(p => p.id === projectId);
      if (!project) return;

      const newProject: Project = {
        ...project,
        id: crypto.randomUUID(),
        name: `${project.name} (Copy)`,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      await db.projects.add(newProject);
      await loadData();
    } catch (error) {
      console.error("Failed to duplicate project:", error);
    }
  }

  async function handleDeleteProject(projectId: string, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm("Are you sure you want to delete this project?")) return;
    
    try {
      await db.projects.delete(projectId);
      await loadData();
    } catch (error) {
      console.error("Failed to delete project:", error);
    }
  }

  const projectsWithStatus: ProjectWithStatus[] = filteredProjects.map(p => ({
    ...p,
    status: getProjectStatus(p),
  }));

  const activeProjects = projectsWithStatus.filter(p => p.status === "active");
  const completedProjects = projectsWithStatus.filter(p => p.status === "completed");
  const archivedProjects = projectsWithStatus.filter(p => p.status === "archived");

  return (
    <>
      {/* Overlay - Show on both mobile and desktop when sidebar is open */}
      <AnimatePresence>
      {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={onClose}
        />
      )}
      </AnimatePresence>

      {/* Sidebar - Can be toggled on desktop and mobile */}
      <motion.div
        initial={false}
        animate={{
          x: isOpen ? 0 : -320,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
        }}
        className="fixed left-0 top-0 bottom-0 w-72 sm:w-80 backdrop-blur-xl border-r border-black/20 z-50"
        style={{
          background: `linear-gradient(135deg, ${profile.brand.primaryColor}15 0%, ${profile.brand.secondaryColor}15 50%, ${profile.brand.primaryColor}15 100%)`,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-black/20 flex items-center justify-between">
            <h2 className="text-black font-semibold text-lg">Chat & Projects</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-black/10 text-black/80 hover:text-black transition"
              title="Close sidebar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* AI Quick Selector + Multi-Chat - Above Tabs */}
          <div className="px-4 py-3 border-b border-black/10 space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <AISelector
                  currentProfile={profile}
                  onSelectAI={(aiId) => {
                    if (onSelectAI) {
                      onSelectAI(aiId);
                    }
                    router.push(`/chat?aiId=${aiId}`);
                  }}
                />
              </div>
              {hasMultiChat && (
                <button
                  onClick={() => {
                    localStorage.setItem("multi_chat_enabled", "true");
                    router.push("/chat?mode=multi");
                    onClose(); // Close sidebar when opening multi-chat
                  }}
                  className="px-3 py-2 rounded-lg transition-all hover:scale-105 active:scale-95 font-medium text-sm flex items-center gap-1.5 flex-shrink-0"
                  style={{
                    background: `linear-gradient(135deg, ${profile.brand.primaryColor} 0%, ${profile.brand.secondaryColor} 100%)`,
                    color: "white",
                    boxShadow: `0 4px 12px ${profile.brand.primaryColor}30`,
                  }}
                  title="Open Multi-Chat Mode"
                >
                  <MessageSquareMore className="w-4 h-4" />
                  <span className="hidden sm:inline">Multi</span>
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-black/10">
            <button
              onClick={() => setActiveTab("projects")}
              className={`flex-1 px-3 py-2.5 text-xs font-medium transition ${
                activeTab === "projects"
                  ? "text-black border-b-2"
                  : "text-black/60 hover:text-black/80"
              }`}
              style={activeTab === "projects" ? { borderBottomColor: profile.brand.primaryColor } : {}}
            >
              <FolderKanban className="w-3.5 h-3.5 inline-block mr-1.5" />
              Projects
            </button>
            <button
              onClick={() => setActiveTab("chats")}
              className={`flex-1 px-3 py-2.5 text-xs font-medium transition ${
                activeTab === "chats"
                  ? "text-black border-b-2"
                  : "text-black/60 hover:text-black/80"
              }`}
              style={activeTab === "chats" ? { borderBottomColor: profile.brand.primaryColor } : {}}
            >
              <MessageSquare className="w-3.5 h-3.5 inline-block mr-1.5" />
              Chats
            </button>
            <button
              onClick={() => setActiveTab("ai")}
              className={`flex-1 px-3 py-2.5 text-xs font-medium transition ${
                activeTab === "ai"
                  ? "text-black border-b-2"
                  : "text-black/60 hover:text-black/80"
              }`}
              style={activeTab === "ai" ? { borderBottomColor: profile.brand.primaryColor } : {}}
            >
              <Bot className="w-3.5 h-3.5 inline-block mr-1.5" />
              AI
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === "projects" ? (
              <div className="space-y-4">
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40" />
                  <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-black/10 bg-white/50 text-sm text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black/20"
                  />
                </div>

                <button
                  onClick={() => {
                    router.push("/projects/new");
                    onClose();
                  }}
                  className="w-full p-3 rounded-lg text-white flex items-center gap-2 transition mb-4 font-medium"
                  style={{
                    background: `linear-gradient(135deg, ${profile.brand.primaryColor} 0%, ${profile.brand.secondaryColor} 100%)`,
                  }}
                >
                  <Plus className="w-4 h-4" />
                  New Project
                </button>

                {filteredProjects.length === 0 ? (
                  <div className="text-center py-8 text-black/60 text-sm">
                    {searchQuery ? "No projects found" : "No projects yet"}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Active Projects */}
                    {activeProjects.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-black/70 uppercase tracking-wide mb-2">Active</h3>
                        <div className="space-y-2">
                          {activeProjects.map((project) => (
                            <div
                              key={project.id}
                              className="group relative"
                            >
                              <button
                                onClick={() => {
                                  if (onSelectProject) {
                                    onSelectProject(project.id);
                                  }
                                  router.push(`/projects/${project.id}`);
                                  onClose();
                                }}
                                className="w-full p-3 rounded-lg hover:bg-black/5 text-left transition text-black/90 hover:text-black border border-black/10"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm mb-1 flex items-center gap-2">
                                      {project.name}
                                      <span className="w-2 h-2 rounded-full bg-green-500" title="Active" />
                                    </div>
                                    {project.description && (
                                      <div className="text-xs text-black/60 line-clamp-1">
                                        {project.description}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedProjectId(expandedProjectId === project.id ? null : project.id);
                                    }}
                                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-black/10 transition"
                                  >
                                    <MoreVertical className="w-4 h-4 text-black/60" />
                                  </button>
                                </div>
                              </button>
                              {expandedProjectId === project.id && (
                                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-black/10 z-10 min-w-[120px]">
                                  <button
                                    onClick={(e) => handleDuplicateProject(project.id, e)}
                                    className="w-full px-3 py-2 text-left text-sm text-black/80 hover:bg-black/5 flex items-center gap-2"
                                  >
                                    <Copy className="w-3.5 h-3.5" />
                                    Duplicate
                                  </button>
                                  <button
                                    onClick={(e) => handleDeleteProject(project.id, e)}
                                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Completed Projects */}
                    {completedProjects.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-black/70 uppercase tracking-wide mb-2">Completed</h3>
                        <div className="space-y-2">
                          {completedProjects.map((project) => (
                            <button
                              key={project.id}
                              onClick={() => {
                                if (onSelectProject) {
                                  onSelectProject(project.id);
                                }
                                router.push(`/projects/${project.id}`);
                                onClose();
                              }}
                              className="w-full p-3 rounded-lg hover:bg-black/5 text-left transition text-black/70 hover:text-black border border-black/10"
                            >
                              <div className="font-medium text-sm mb-1 flex items-center gap-2">
                                {project.name}
                                <span className="w-2 h-2 rounded-full bg-yellow-500" title="Completed" />
                              </div>
                              {project.description && (
                                <div className="text-xs text-black/50 line-clamp-1">
                                  {project.description}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Archived Projects */}
                    {archivedProjects.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-black/70 uppercase tracking-wide mb-2">Archived</h3>
                        <div className="space-y-2">
                          {archivedProjects.map((project) => (
                            <button
                              key={project.id}
                              onClick={() => {
                                if (onSelectProject) {
                                  onSelectProject(project.id);
                                }
                                router.push(`/projects/${project.id}`);
                                onClose();
                              }}
                              className="w-full p-3 rounded-lg hover:bg-black/5 text-left transition text-black/60 hover:text-black/80 border border-black/10"
                            >
                              <div className="font-medium text-sm mb-1 flex items-center gap-2">
                                {project.name}
                                <span className="w-2 h-2 rounded-full bg-gray-400" title="Archived" />
                              </div>
                              {project.description && (
                                <div className="text-xs text-black/50 line-clamp-1">
                                  {project.description}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : activeTab === "chats" ? (
              <div className="space-y-4">
                {/* Search */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/40" />
                  <input
                    type="text"
                    placeholder="Search chats..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-black/10 bg-white/50 text-sm text-black placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black/20"
                  />
                </div>

                <button
                  onClick={() => {
                    if (onNewChat) {
                      onNewChat();
                    }
                    onClose();
                  }}
                  className="w-full p-3 rounded-lg text-white flex items-center gap-2 transition mb-4 font-medium"
                  style={{
                    background: `linear-gradient(135deg, ${profile.brand.primaryColor} 0%, ${profile.brand.secondaryColor} 100%)`,
                  }}
                >
                  <Plus className="w-4 h-4" />
                  New Chat
                </button>

                {/* Direct AI Chats - Top Half */}
                <div className="space-y-2">
                  <button
                    onClick={() => setShowDirectChats(!showDirectChats)}
                    className="flex items-center gap-2 mb-2 w-full text-left"
                  >
                    {showDirectChats ? <ChevronDown className="w-4 h-4 text-black/60" /> : <ChevronUp className="w-4 h-4 text-black/60" />}
                    <Bot className="w-4 h-4 text-black/60" />
                    <h3 className="text-xs font-semibold text-black/70 uppercase tracking-wide flex-1">Direct Chats</h3>
                    <span className="text-xs text-black/50">{Array.from(filteredDirectChats.values()).flat().length}</span>
                  </button>
                  {showDirectChats && (
                    <>
                      {Array.from(filteredDirectChats.entries()).length === 0 ? (
                        <div className="text-center py-4 text-black/50 text-xs">
                          No direct chats
                        </div>
                      ) : (
                        Array.from(filteredDirectChats.entries()).map(([aiId, chats]) => {
                          const ai = allAIs.find(a => a.id === aiId);
                          return (
                            <div key={aiId} className="mb-3">
                              <div className="text-xs font-medium text-black/70 mb-1.5 px-2">
                                {ai?.name || "Unknown AI"}
                              </div>
                              <div className="space-y-1.5">
                                {chats.slice(0, 3).map((chat) => (
                                  <button
                                    key={chat.id}
                                    onClick={() => {
                                      if (onSelectAI && chat.aiId) {
                                        onSelectAI(chat.aiId);
                                      }
                                      onClose();
                                    }}
                                    className="w-full p-2.5 rounded-lg hover:bg-black/5 text-left transition text-black/90 hover:text-black border border-black/10"
                                  >
                                    <div className="flex items-start gap-2">
                                      <Bot className="w-3.5 h-3.5 mt-0.5 text-black/60 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs line-clamp-2 mb-1">{chat.preview}</div>
                                        <div className="flex items-center gap-1 text-xs text-black/50">
                                          <Clock className="w-3 h-3" />
                                          {new Date(chat.timestamp).toLocaleDateString()}
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </>
                  )}
                </div>

                {/* Group/Team Chats - Bottom Half */}
                <div className="space-y-2 pt-4 border-t border-black/10">
                  <button
                    onClick={() => setShowTeamChats(!showTeamChats)}
                    className="flex items-center gap-2 mb-2 w-full text-left"
                  >
                    {showTeamChats ? <ChevronDown className="w-4 h-4 text-black/60" /> : <ChevronUp className="w-4 h-4 text-black/60" />}
                    <Users className="w-4 h-4 text-black/60" />
                    <h3 className="text-xs font-semibold text-black/70 uppercase tracking-wide flex-1">Team Chats</h3>
                    <span className="text-xs text-black/50">{filteredGroupChats.length}</span>
                  </button>
                  {showTeamChats && (
                    <>
                      {filteredGroupChats.length === 0 ? (
                        <div className="text-center py-4">
                          {teams.length === 0 ? (
                            <div className="space-y-2">
                              <p className="text-black/50 text-xs mb-3">No team chats yet</p>
                              <button
                                onClick={() => {
                                  router.push("/team");
                                  onClose();
                                }}
                                className="w-full p-2.5 rounded-lg text-white text-xs font-medium transition"
                                style={{
                                  background: `linear-gradient(135deg, ${profile.brand.primaryColor} 0%, ${profile.brand.secondaryColor} 100%)`,
                                }}
                              >
                                <Plus className="w-3.5 h-3.5 inline-block mr-1.5" />
                                Create Team
                              </button>
                            </div>
                          ) : (
                            <p className="text-black/50 text-xs">No team chats yet</p>
                          )}
                        </div>
                      ) : (
                        filteredGroupChats.map((chat) => (
                          <button
                            key={`${chat.teamId}-${chat.channelId}`}
                            onClick={() => {
                              if (onSelectTeamChannel) {
                                onSelectTeamChannel(chat.teamId, chat.channelId);
                              }
                              router.push(`/team?teamId=${chat.teamId}&channelId=${chat.channelId}`);
                              onClose();
                            }}
                            className="w-full p-2.5 rounded-lg hover:bg-black/5 text-left transition text-black/90 hover:text-black border border-black/10"
                          >
                            <div className="flex items-start gap-2">
                              <Hash className="w-3.5 h-3.5 mt-0.5 text-black/60 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium mb-0.5">{chat.name}</div>
                                <div className="text-xs line-clamp-1 text-black/60 mb-1">{chat.lastMessage}</div>
                                <div className="flex items-center gap-1 text-xs text-black/50">
                                  <Clock className="w-3 h-3" />
                                  {new Date(chat.timestamp).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              // AI Tab - List all AIs for quick switching
              <div className="space-y-2">
                <button
                  onClick={() => {
                    router.push("/builder");
                    onClose();
                  }}
                  className="w-full p-3 rounded-lg text-white flex items-center gap-2 transition mb-4 font-medium"
                  style={{
                    background: `linear-gradient(135deg, ${profile.brand.primaryColor} 0%, ${profile.brand.secondaryColor} 100%)`,
                  }}
                >
                  <Plus className="w-4 h-4" />
                  Create New AI
                </button>
                {allAIs.length === 0 ? (
                  <div className="text-center py-8 text-black/60 text-sm">
                    No AIs yet
                  </div>
                ) : (
                  allAIs.map((ai) => (
                    <button
                      key={ai.id}
                      onClick={() => {
                        if (onSelectAI) {
                          onSelectAI(ai.id);
                        }
                        router.push(`/chat?aiId=${ai.id}`);
                        onClose();
                      }}
                      className={`w-full p-3 rounded-lg text-left transition border ${
                        ai.id === profile.id
                          ? "bg-black/10 border-black/20"
                          : "hover:bg-black/5 border-black/10"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Bot className={`w-4 h-4 ${ai.id === profile.id ? "text-black" : "text-black/60"}`} />
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium text-sm ${ai.id === profile.id ? "text-black" : "text-black/90"}`}>
                            {ai.name}
                          </div>
                          {ai.description && (
                            <div className="text-xs text-black/60 line-clamp-1 mt-0.5">
                              {ai.description}
                            </div>
                          )}
                        </div>
                        {ai.id === profile.id && (
                          <div className="text-xs px-2 py-0.5 rounded bg-black/10 text-black/70 font-medium">
                            Active
                          </div>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
}
